'use client'

import * as React from 'react'
import type { Clubhouse } from 'codegen/types'
import Image from 'next/image'
import { useTranslations } from 'next-intl'
import { Link } from '@repo/web-ui/components/link'
import { Tooltip } from 'primitives/tooltip'
import { tw } from 'utils/classnames'
import { useBreakpoint } from '@repo/web-ui/hooks/use-breakpoints'
import { useClubhousesFromUserQuery } from '@web/gen/crafter'
import { useUser } from '@repo/cf-user/hooks/use-user-data'
import {
  IconBookmarks,
  IconPhotoPrints,
  IconExplore,
} from '@repo/design-system-cf/components/icons'
import { AppRoutes, AppRoutesParameterized } from '@repo/web-utils/app-routes'
import { AmplitudeEvents, sendAmplitudeEvent } from '@repo/analytics/amplitude/utils'

// oxlint-disable-next-line complexity
export function ClubhouseSwitcher({
  clubhouseSlug,
  activeFeed = null,
  userSlug = null,
}: {
  clubhouseSlug?: string
  activeFeed?: 'projects' | 'saved' | 'user' | null
  userSlug?: string | null
}) {
  const t = useTranslations('pages.clubhouse')
  const user = useUser()
  const { breakpoint } = useBreakpoint()
  const { data: clubhouses } = useClubhousesFromUserQuery(
    {
      input: { userId: user.data?.id || '', page: 0, itemsPerPage: 15 },
    },
    { select: data => data?.clubhousesFromUser?.clubhouses },
  )

  const isMobile = ['xs', 'sm', 'md'].includes(breakpoint ?? '')

  return (
    <div className="relative flex">
      <div className="scroller no-scrollbar top-6 pr-[148px] pl-4 sm:pl-0 lg:pr-0 flex max-w-[100vw] shrink-0 flex-row gap-3 lg:gap-0 overflow-auto lg:sticky lg:max-h-screen lg:max-w-none lg:flex-col lg:self-start lg:pl-0 lg:z-10">
        <div className="hidden lg:flex group w-12 flex-col gap-2 lg:h-[61px] lg:w-auto lg:flex-row lg:items-stretch lg:gap-4">
          <div
            className={tw(
              'rounded-b-2 lg:rounded-r-2 h-1 w-full lg:h-12 lg:w-1 lg:rounded-b-none',
              activeFeed === 'projects'
                ? 'bg-legacy-cf-purple-100'
                : 'bg-transparent group-hover:bg-[#DADCE2]',
            )}
          />
          <Tooltip
            key="progects-feed-item"
            value={
              <div
                className="relative rounded-[4px]"
                style={{
                  boxShadow: '0px 2px 8px 0px #00000026, 0px 0px 1px 0px #0000004D',
                }}
              >
                {/* Arrow for side="right" (shown on lg and up) */}
                <div className="bg-cf-sf-gray-75 absolute left-[-3px] top-1/2 hidden size-1.5 -translate-y-1/2 rotate-45 border-b border-l border-[#0A0A0B1A] lg:block" />
                {/* Arrow for side="bottom" (shown on small screens) */}
                <div className="bg-cf-sf-gray-75 absolute left-1/2 top-[-3px] block size-1.5 -translate-x-1/2 rotate-45 border-l border-t border-[#0A0A0B1A] lg:hidden" />
                <div className=" bg-cf-sf-gray-75 rounded-[4px] border border-[#0A0A0B1A] px-2 py-1.5">
                  <p className="text-14 text-cf-sf-gray-1100 text-center font-bold">
                    Projects Feed
                  </p>
                </div>
              </div>
            }
            delay={0}
            sideOffset={10}
            className="bg-transparent p-0"
            // oxlint-disable-next-line no-negated-condition
            side={!isMobile ? 'right' : 'bottom'}
            hideArrow={true}
          >
            <div className="border-b border-[#1313151F] h-[62px] mb-4 ">
              <Link
                href={`${AppRoutes.SOCIAL_CURATED_FEED}`}
                className="rounded-full flex justify-center relative items-center size-12 bg-cf-purple-100"
                aria-label="Go to discovery feed link"
              >
                <div className="absolute h-[1px] w-12 bg-[#1313151F] -top-4 hidden lg:block" />
                <IconPhotoPrints className="size-7 text-cf-purple-900" />
                <div className="absolute inset-0 pointer-events-none rounded-full border border-[#1313151F]" />
              </Link>
            </div>
          </Tooltip>
        </div>
        <div className="flex lg:flex-col lg:gap-4 gap-3 overflow-auto no-scrollbar lg:pb-[180px] lg:pt-4">
          {clubhouses?.map(clubhouse => {
            const bgColor = clubhouse.lightBgColor || '#E2E4E9'
            return (
              <Tooltip
                key={clubhouse.slug}
                value={
                  <div
                    className="relative rounded-[4px]"
                    style={{ boxShadow: '0px 2px 8px 0px #00000026, 0px 0px 1px 0px #0000004D' }}
                  >
                    {/* Arrow for side="right" (shown on lg and up) bg-cf-grey-50 */}
                    <div
                      style={{
                        backgroundColor: bgColor,
                      }}
                      className="absolute left-[-3px] bg- top-1/2 hidden size-1.5 -translate-y-1/2 rotate-45 border-b border-l border-[#0A0A0B1A] lg:block"
                    />

                    {/* Arrow for side="bottom" (shown on small screens) */}
                    <div
                      style={{
                        backgroundColor: bgColor,
                      }}
                      className="absolute left-1/2 top-[-3px] block size-1.5 -translate-x-1/2 rotate-45 border-l border-t border-[#0A0A0B1A] lg:hidden"
                    />
                    <div
                      style={{
                        backgroundColor: bgColor,
                      }}
                      className=" bg-cf-sf-gray-75 rounded-[4px] border border-[#0A0A0B1A] px-2 py-1.5"
                    >
                      <p className="text-14 text-cf-sf-gray-1100 text-center font-bold">
                        {clubhouse.name}
                      </p>
                    </div>
                  </div>
                }
                delay={0}
                sideOffset={10}
                className="bg-transparent p-0"
                // oxlint-disable-next-line no-negated-condition
                side={!isMobile ? 'right' : 'bottom'}
                hideArrow={true}
              >
                <div>
                  <ClubhouseItem
                    key={clubhouse.slug}
                    slug={clubhouse.slug}
                    selectedClubhouseSlug={clubhouseSlug || null}
                    clubhouse={clubhouse}
                  />
                </div>
              </Tooltip>
            )
          })}
          <Tooltip
            value={
              <div
                className="relative rounded-[4px]"
                style={{ boxShadow: '0px 2px 8px 0px #00000026, 0px 0px 1px 0px #0000004D' }}
              >
                {/* Arrow for side="right" (shown on lg and up) */}
                <div className="bg-cf-sf-gray-75 absolute left-[-3px] top-1/2 hidden size-1.5 -translate-y-1/2 rotate-45 border-b border-l border-[#0A0A0B1A] lg:block" />
                {/* Arrow for side="bottom" (shown on small screens) */}
                <div className="bg-cf-sf-gray-75 absolute left-1/2 top-[-3px] block size-1.5 -translate-x-1/2 rotate-45 border-l border-t border-[#0A0A0B1A] lg:hidden" />
                <div className=" bg-cf-sf-gray-75 rounded-[4px] border border-[#0A0A0B1A] px-2 py-1.5">
                  <p className="text-14 text-cf-sf-gray-1100 text-center font-bold">
                    {t('clubhouse_homepage.switcher.discover_title')}
                  </p>
                </div>
              </div>
            }
            delay={0}
            sideOffset={10}
            className="bg-transparent p-0"
            // oxlint-disable-next-line no-negated-condition
            side={!isMobile ? 'right' : 'bottom'}
            hideArrow={true}
          >
            <div className="group flex w-12 flex-col gap-2 lg:h-12 lg:w-auto lg:flex-row lg:items-stretch lg:gap-4 mr-4 lg:mr-0">
              <div
                className={tw(
                  'rounded-b-2 lg:rounded-r-2 h-1 w-full group-hover:bg-[#DADCE2] lg:h-full lg:w-1 lg:rounded-b-none',
                )}
              />
              <Link
                className="flex h-12 w-12 items-center rounded-full bg-[#E2E4E9] p-2.5"
                href={AppRoutes.CLUBHOUSE}
                aria-label="Go to clubhouse discovery page link"
              >
                <IconExplore />
              </Link>
            </div>
          </Tooltip>
        </div>
      </div>

      {user.data?.avatarUrl ? (
        <div className="lg:relative lg:-ml-[68px] absolute right-0">
          <div className="lg:sticky lg:top-[calc(100vh-140px)] lg:left-0 lg:w-[68px] lg:z-20 bg-cf-grey-50">
            <div className="flex lg:flex-row flex-col">
              <div className="flex lg:flex-col">
                <div className="border-l border-[#1313151F] mt-3 lg:mt-0 lg:border-0 lg:pt-4 lg:pl-5 pl-3 group relative">
                  <div
                    className={tw(
                      'absolute -top-3 lg:left-0 lg:top-auto rounded-b-2 lg:rounded-r-2 h-1 w-[48px] lg:h-[48px] lg:w-1 lg:rounded-b-none',
                      activeFeed === 'saved'
                        ? 'bg-legacy-cf-purple-100'
                        : 'bg-transparent group-hover:bg-[#DADCE2]',
                    )}
                  />
                  <Tooltip
                    value={
                      <div
                        className="relative rounded-[4px]"
                        style={{
                          boxShadow: '0px 2px 8px 0px #00000026, 0px 0px 1px 0px #0000004D',
                        }}
                      >
                        {/* Arrow for side="right" (shown on lg and up) */}
                        <div className="bg-cf-sf-gray-75 absolute left-[-3px] top-1/2 hidden size-1.5 -translate-y-1/2 rotate-45 border-b border-l border-[#0A0A0B1A] lg:block" />
                        {/* Arrow for side="bottom" (shown on small screens) */}
                        <div className="bg-cf-sf-gray-75 absolute left-1/2 top-[-3px] block size-1.5 -translate-x-1/2 rotate-45 border-l border-t border-[#0A0A0B1A] lg:hidden" />
                        <div className=" bg-cf-sf-gray-75 rounded-[4px] border border-[#0A0A0B1A] px-2 py-1.5">
                          <p className="text-14 text-cf-sf-gray-1100 text-center font-bold">
                            Saved posts
                          </p>
                        </div>
                      </div>
                    }
                    delay={0}
                    sideOffset={10}
                    className="bg-transparent p-0"
                    // oxlint-disable-next-line no-negated-condition
                    side={!isMobile ? 'right' : 'bottom'}
                    hideArrow={true}
                  >
                    <Link
                      href={`${AppRoutes.SOCIAL_CURATED_FEED_SAVED}`}
                      className="rounded-full flex justify-center relative items-center size-12 bg-cf-purple-100"
                      aria-label="Go to saved posts link"
                    >
                      <div className="absolute h-[1px] w-12 bg-[#1313151F] -top-4 hidden lg:block" />
                      <IconBookmarks className="size-7 text-cf-purple-900" />
                      <div className="absolute inset-0 pointer-events-none rounded-full border border-[#1313151F]" />
                    </Link>
                  </Tooltip>
                </div>
                <div className="lg:pl-5 flex justify-center pt-3 pl-3 lg:px-0 group relative pr-4 sm:pr-0 lg:mb-8">
                  <div
                    className={tw(
                      'absolute top-0 lg:left-0 lg:top-auto rounded-b-2 lg:rounded-r-2 h-1 w-[48px] lg:h-[48px] lg:w-1 lg:rounded-b-none',
                      activeFeed === 'user' && userSlug === user.data?.slug
                        ? 'bg-legacy-cf-purple-100'
                        : 'bg-transparent group-hover:bg-[#DADCE2]',
                    )}
                  />
                  <Tooltip
                    value={
                      <div
                        className="relative rounded-[4px]"
                        style={{
                          boxShadow: '0px 2px 8px 0px #00000026, 0px 0px 1px 0px #0000004D',
                        }}
                      >
                        {/* Arrow for side="right" (shown on lg and up) */}
                        <div className="bg-cf-sf-gray-75 absolute left-[-3px] top-1/2 hidden size-1.5 -translate-y-1/2 rotate-45 border-b border-l border-[#0A0A0B1A] lg:block" />
                        {/* Arrow for side="bottom" (shown on small screens) */}
                        <div className="bg-cf-sf-gray-75 absolute left-1/2 top-[-3px] block size-1.5 -translate-x-1/2 rotate-45 border-l border-t border-[#0A0A0B1A] lg:hidden" />
                        <div className=" bg-cf-sf-gray-75 rounded-[4px] border border-[#0A0A0B1A] px-2 py-1.5">
                          <p className="text-14 text-cf-sf-gray-1100 text-center font-bold">
                            My profile
                          </p>
                        </div>
                      </div>
                    }
                    delay={0}
                    sideOffset={10}
                    className="bg-transparent p-0"
                    // oxlint-disable-next-line no-negated-condition
                    side={!isMobile ? 'right' : 'bottom'}
                    hideArrow={true}
                  >
                    <Link
                      href={AppRoutes.SOCIAL_FEED_CRAFTER + user.data?.slug}
                      className="relative rounded-[12px] overflow-hidden"
                      aria-label="Go to my profile link"
                    >
                      <Image
                        src={user.data?.avatarUrl}
                        alt={`${user.data?.firstName} ${user.data?.lastName}`}
                        width={48}
                        height={48}
                      />
                      <div className="absolute inset-0 pointer-events-none rounded-[12px] border border-[#1313151F]" />
                    </Link>
                  </Tooltip>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}

const ClubhouseItem = ({
  slug,
  selectedClubhouseSlug,
  clubhouse,
}: {
  slug: string
  selectedClubhouseSlug: string | null
  clubhouse: Clubhouse
}) => {
  return (
    <div className="group flex w-12 flex-col gap-2 lg:h-12 lg:w-auto lg:flex-row lg:items-stretch lg:gap-4">
      <div
        className={tw(
          'rounded-b-2 lg:rounded-r-2 h-1 w-full lg:h-full lg:w-1 lg:rounded-b-none',
          selectedClubhouseSlug === slug
            ? 'bg-legacy-cf-purple-100'
            : 'bg-transparent group-hover:bg-[#DADCE2]',
        )}
      />
      <Link
        className={tw(
          'flex h-12 w-12 items-center justify-center rounded-full border-[#0A0A0B1A] group-hover:opacity-60 group-active:opacity-80',
          clubhouse.bgColor ? `bg-[${clubhouse?.bgColor}]` : 'bg-cf-white',
        )}
        href={AppRoutesParameterized.CLUBHOUSE(slug)}
        onClick={() => {
          sendAmplitudeEvent(AmplitudeEvents.CLUBHOUSE_SWITCHER_CLICKED, {
            title: clubhouse.name,
          })
        }}
      >
        <Image src={clubhouse?.avatarImage || ''} alt="logo" width={66} height={66} />
      </Link>
    </div>
  )
}
